/**
 * InteractiveChartHint — explicit "this chart is clickable" affordance.
 *
 * Motivation (v1.4 user report, 2026-04-24): "нужно явно заметно, чтобы там
 * в заголовке было написано, что кликабл". The hover-stroke + permanent
 * touch outline added in 938b5c85 convey that a bar is *currently hovered*
 * / *tappable*, but users still reported that neither desktop nor mobile
 * signals "you can click this" without prior knowledge — the user may not
 * think to try.
 *
 * Fix: pin a compact chip in the chart card header with an explicit icon
 * and verb. Two visual cues instead of one:
 *   - Icon (MousePointerClick) — universal pointer-on-target glyph, reads
 *     as "interactive" even before the text lands.
 *   - Verb — "Click to drilldown" on pointer devices, "Tap to drilldown" on
 *     touch-only devices (same detection SSOT as tooltip trigger). Copy
 *     was rebranded from "filter" → "drilldown" after user feedback
 *     2026-04-24: "filter" misread as the filter bar; "drilldown"
 *     accurately describes opening a modal / navigating to a sub-view.
 *
 * Visual is deliberately subtle (dashed border, muted foreground, xs text)
 * so it reads as a secondary label next to the CardTitle rather than
 * competing with it. Chips never sit inside the plot area — callers mount
 * them in the existing CardHeader flex row next to the title.
 */

import { MousePointerClick } from 'lucide-react';
import * as React from 'react';

import { useIsTouchOnly } from '@/components/charts/ChartTooltip';
import { cn } from '@/lib/utils';

export interface InteractiveChartHintProps {
  /**
   * Override the verb phrase. Defaults to "Click to drilldown" / "Tap to
   * drilldown" based on pointer mode. Pass an explicit string when the
   * drill-down semantics aren't drilldown-shaped (e.g. "Click to open").
   */
  label?: string;
  /** Extra classes forwarded to the wrapper for caller layout tweaks. */
  className?: string;
  /**
   * data-testid override — defaults to `chart-interactive-hint`. Useful
   * when multiple hints sit on one page and the test needs to target a
   * specific chart.
   */
  'data-testid'?: string;
}

export function InteractiveChartHint({
  label,
  className,
  'data-testid': dataTestId = 'chart-interactive-hint',
}: InteractiveChartHintProps): React.ReactElement {
  const isTouch = useIsTouchOnly();
  const text = label ?? (isTouch ? 'Tap to drilldown' : 'Click to drilldown');
  return (
    <span
      data-testid={dataTestId}
      data-pointer-mode={isTouch ? 'touch' : 'pointer'}
      className={cn(
        'inline-flex items-center gap-1 rounded-full border border-dashed',
        'px-2 py-0.5 text-[11px] font-medium',
        'text-primary/90 motion-safe:animate-hint-pulse motion-reduce:bg-primary/10 motion-reduce:border-primary/60',
        'whitespace-nowrap',
        className,
      )}
    >
      <MousePointerClick className="h-3 w-3 shrink-0" aria-hidden />
      {text}
    </span>
  );
}
